<template>
  <div
    v-if="store.enabled && store.canGoLive && !onPhone"
    class="flex items-center justify-between gap-3 rounded-lg px-4 py-2.5"
  >
    <div class="min-w-0 text-sm">
      <div class="font-medium text-neutral-800">Live</div>
      <div class="text-xs text-neutral-500">{{ hint }}</div>
    </div>
    <FormToggle
      :model-value="store.isLive"
      :is-disabled="store.isSaving"
      size="small"
      aria-label="Live"
      @update:model-value="change"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import FormToggle from '@/shared/components/forms/FormToggle.vue'
import { useInstantSessionsStore } from '@/modules/instant-sessions/stores/instantSessions'
import { isPhoneBrowser } from '@/modules/instant-sessions/utils/isPhoneBrowser'
import {
  GO_LIVE_ERRORS,
  LIVE_OFF_REASONS,
} from '@/modules/instant-sessions/utils/liveText'

const store = useInstantSessionsStore()

// Hidden until mounted, so a phone never sees the toggle flash in.
const onPhone = ref(true)
onMounted(() => {
  onPhone.value = isPhoneBrowser()
})
const error = ref<string | null>(null)

const hint = computed(() => {
  if (error.value) return error.value
  if (store.isLive && store.liveUntil) {
    const until = new Date(store.liveUntil).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    })
    return `Live until ${until} · 15 min calls`
  }
  if (store.liveOffReason) return LIVE_OFF_REASONS[store.liveOffReason] ?? 'Off'
  return 'Members can ask you for a 15 min call'
})

const change = async (live: boolean) => {
  error.value = null
  if (live) {
    store.requestGoLive()
    return
  }
  const result = await store.setLive(false)
  if (!result.ok)
    error.value = GO_LIVE_ERRORS[result.error ?? ''] ?? 'Try again'
}
</script>
